import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ptBR, enUS, es, Locale } from 'date-fns/locale';
import { useUserUIPreferences, useAllUsersUIPreferences } from './useUserUIPreferences';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, type SupportedLanguage } from '@/locales';

// Date format patterns by language
const DATE_FORMATS: Record<SupportedLanguage, {
  short: string;
  long: string;
  dateTime: string;
  dateTimeShort: string;
  time: string;
  timeWithSeconds: string;
  inputDate: string;
}> = {
  'pt-BR': {
    short: 'dd/MM/yyyy',
    long: "d 'de' MMMM 'de' yyyy",
    dateTime: "d 'de' MMMM 'de' yyyy 'às' h:mm a",
    dateTimeShort: "dd/MM/yyyy 'às' HH:mm",
    time: 'HH:mm',
    timeWithSeconds: 'HH:mm:ss',
    inputDate: 'yyyy-MM-dd',
  },
  'en-US': {
    short: 'MM/dd/yyyy',
    long: 'MMMM d, yyyy',
    dateTime: "MMMM d, yyyy 'at' h:mm a",
    dateTimeShort: "MM/dd/yyyy 'at' h:mm a",
    time: 'h:mm a',
    timeWithSeconds: 'h:mm:ss a',
    inputDate: 'yyyy-MM-dd',
  },
  'es': {
    short: 'dd/MM/yyyy',
    long: "d 'de' MMMM 'de' yyyy",
    dateTime: "d 'de' MMMM 'de' yyyy 'a las' HH:mm",
    dateTimeShort: "dd/MM/yyyy 'a las' HH:mm",
    time: 'HH:mm',
    timeWithSeconds: 'HH:mm:ss',
    inputDate: 'yyyy-MM-dd',
  },
};

// date-fns locale mapping
const DATE_FNS_LOCALES: Record<SupportedLanguage, Locale> = {
  'pt-BR': ptBR,
  'en-US': enUS,
  'es': es,
};

export const useUserLanguage = () => {
  const { i18n } = useTranslation();
  const { preferences, isLoading } = useUserUIPreferences();
  const { updatePreferences } = useAllUsersUIPreferences();

  // Get current language from preferences or default
  const language = useMemo<SupportedLanguage>(() => {
    const lang = preferences?.language;
    if (lang && SUPPORTED_LANGUAGES.some(l => l.code === lang)) {
      return lang as SupportedLanguage;
    }
    return DEFAULT_LANGUAGE;
  }, [preferences?.language]);

  // Get date-fns locale for current language
  const dateLocale = useMemo(() => DATE_FNS_LOCALES[language], [language]);

  // Get date formats for current language
  const dateFormats = useMemo(() => DATE_FORMATS[language], [language]);

  // Check if using 24h time format
  const use24HourTime = useMemo(() => language !== 'en-US', [language]);

  // Set language and persist to preferences
  const setLanguage = useCallback(async (newLanguage: SupportedLanguage) => {
    if (!preferences?.user_email) return;
    
    // Update i18next
    await i18n.changeLanguage(newLanguage);
    
    // Persist to database
    await updatePreferences.mutateAsync({
      userEmail: preferences.user_email,
      updates: { language: newLanguage }
    });
  }, [i18n, preferences?.user_email, updatePreferences]);

  // Sync i18n language with preferences
  useMemo(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  return {
    language,
    setLanguage,
    dateLocale,
    dateFormats,
    use24HourTime,
    isLoading,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
};

export type { SupportedLanguage };
